import { useState, useEffect } from "react";

// A real transaction becomes visible once it is at least this old (by its
// on-chain block time). The delay covers a full poll interval plus the
// proxy's 15s edge cache, so by the time a row appears every visitor's
// pool already contains it — all systems show the same rows at the same
// time. Nobody can tell the feed runs ~35s behind the chain.
const REVEAL_MS = 35000;

class DataService {
  constructor() {
    this.solPrice = 0;
    this.exchanges = { Coinbase: 0, Kraken: 0, OKX: 0, Binance: 0, Gate: 0 };
    this.ticker = [];
    this.pumpTrades = [];
    this.botLogs = [];
    this.radarTokens = [];
    this.solSwaps = [];
    this.aiSwaps = [];
    this.logIdx = 0;
    
    // Rolling pools of parsed real transactions (merged across fetches,
    // keyed by signature). The visible lists are recomputed every second
    // as a pure function of these pools + the wall clock, so every
    // visitor sees the same rows at the same time.
    this.latestRealSwaps = [];
    this.latestRealAi = [];
    this.latestRealPump = [];

    // Per-signature arbitrage pricing cache so a row's PnL/venue doesn't
    // fluctuate as live CEX quotes move between recomputes.
    this.arbCache = new Map();

    // Live-data flags: when a real feed is healthy, the corresponding
    // simulated generator pauses; if a feed goes down, simulation resumes.
    this.priceIsLive = false;
    this.txLive = false;
    this.aiLive = false;
    this.pumpLive = false;
    this.radarLive = false;
    this.aiFailStreak = 0;

    // Seed-once guards and failure tolerance: a single failed poll must
    // not re-seed the visible lists (that would reset every row back to
    // IN_PROGRESS) or hand control back to the simulators.
    this.pumpSeeded = false;
    this.swapFailStreak = 0;
    this.pumpFailStreak = 0;

    // Per-feed signature pools so the arbitrage simulator never links to
    // a pump.fun token transaction and vice versa.
    this.swapSigPool = [];
    this.pumpSigPool = [];

    // Live CEX spot prices for SOL (name -> { price, ts }), fetched from
    // each exchange's public keyless API. Used to compute real arbitrage
    // spreads/PnL; entries older than 30s are treated as stale.
    this.cexPrices = {};

    // Loss pacing for realized pump PnL (~1 losing exit per 25 rows).
    this.rowsSinceLoss = 0;

    // Until the first real fetch succeeds, realSigs holds the bundled
    // fallback signatures; the first live batch replaces them entirely so
    // nothing on the site links to stale transactions once real data flows.
    this.realSigsSeeded = false;

    // mint -> { name, ticker, image } for tokens seen via Jupiter's recent
    // token API, used to label real pump.fun trades.
    this.mintProfiles = {};
    
    // Real Solana transaction signatures and token profiles
    this.realSigs = [
      "2efuQ9fQSWXAtKWnvboP4vmukaMABBG8iybLRHjWod5qmNzYnde5hdRRhtsUEYkhx4rXPLp5K7JjV8ishrDeusbQ",
      "4oi91ci2ag78DBS6Qddw15PNz2afiRRmfV4i7QPfNsV9r1D4eNCtoaH5fS7KaTSNjdxA5ZUwJjo57sCzeBzm78Wf",
      "AA5NBh7c1LFcg5fkERpnzuN5FA1yPyCv6QB3GMoijGiZqs3SB6M8gnBzVPt7rgQu9NVteLft7H8BLHz6RQ8i91r",
      "aPVpLtYVyNYoHXw32PU2U4ijTwUYSjnQ1HUmzjZfySBBUGxH6UfKRwu3jv1RPocA5GoU1F2hXGVVsXgPyFV9QA9",
      "648bnEDTPzRy34f7n1qUzYo2bxAJqerrE69JQ8YdNBgqiyQDKrMyxSd3x6Sho3MKzgexrHMguGGdHPxFs5QpKygu",
      "3w5HV1mnUgz9gZrUvCXSv3fvjWqsUY7V8aWNeib6k4C6CmtTDP2WWGFAdJCcmfHVDsN3U6N14MLWjZ5XhEEHknt8",
      "5ZBauabEV9xDkBDU3z7EX6FTM1sbXwnsg8h6pKSEDduAM7AAfZcpLBA6YS7CkB2h17PgRTx42dVXYAsUfxBPMJcr",
      "4y8WT6bqbsr3HVjVbKUhEQ3eVBVwCXsQmx2tbERKdE3BSVYrCWSBR4RSoT7kHrXb9XACTQkEGnZsw5gUw8wHYpTx",
      "36UXrEZkvrcvww6tPGcEx2EG8qSpM5AAnfSrz1PrNHaiqwppjnCjwo1F1engJiC4G84ALZP4CDfGrtnWrqoLu92X",
      "4Tc7p2EvE6C8HZNYNMMSGLBuRXb9SFJdvTc6izTfzDUJhQcTMaGsWbpotrTaxPSV3g9kxACzri59zXzk8YvSDh3Z",
      "3tfbJNm755XKUnW77aCsKxeQXy8KuAwmwqgxPK7FiCqmFLdadg3kGS92nmGvA34svetZ2h8AjpvGEEfBanixdVtS",
      "2NnN4xG54GhYRN7pbv9gX81Tz3qymyYRATviwT5kU6FNdAXEEyX6UbJvnumLkLKm2iuDhCu2zAM5M92GPoF9EXqg",
      "5khqqsQK3QstnMDH12Xz17yYvd4aDhPvoaHLLTRWweQEBzfTUpaociXH5XWsamknGZtRFsMbNmRLazs7TdhMaLc8",
      "5nh5eELtKkTJXWTU3qKxEheRDVAey5vWcg7PEN5kXVCLWSyv4aiJPGkeEPeqD57qTZUcbVqqTfP1QeqypfjtYonU",
      "5Wh2jNTuENY7mdSe3agm7n8W1YrdaZuZv3vKb2wrKXq2gLRzSZgMbz9nm2Nipa3NTh2esoVLYdpMWMhA5cLz6dzW",
      "5eo5PEhBb97Xmwvi33rmLCYdBATYKTV25HPPeshW4jo2q4jR3BNmoh4FoWet2oJPjAk5rZPDoM8yvuxJcFwfUxc5",
      "AHb5T2Ur1P5Ey9jXuThKPuscU7KgEcywurN6LbwRkShomDtCdZTNBrv6HTjvUqLtgA5Hg3srWPwPB63Pnysnjjv",
      "3o7A7Z2joXDv6keEURpFEVmwpo1tRkcaDXbLRrzEPN8hu8HbpN4WHCRFgSPQSGcrJ4v3wDCuAPA4YsVkpYqB9QfJ",
      "2v7rHNxiMnBhueR7VWWdpj3GKBEaZ7u8U5R7YZ9xuaKZFM6wdbW1NGgrHqCwwfPag8oeNb3e5YTJk39Qv6moae3P",
      "hMTqVdK66dJxn1qwbsWJfJjm8D62yMQosPUrwsHWeeJpRSYGKM8GffhMxnVPUK6JMpxf5fpnhD5ujMGMDTxoaiz",
      "3CMH2x4oWYFwzCLDsUNCVXoLhYdBYx3iyXUhzs2smbpyo3zqR2YnjAB4hBE7kWDToMakbfK61Wszk1aFavBvjYNA",
      "2CeWXacSTkvHJ2WMvgX486wusY6frAiUcSP6Zahpnx1JSv76nSqNm4P5oGDZPU3d8fNBSbKEVmWcsUzXQZBDvia4",
      "4NHTAL5Uij7ZLCU2Fa8SPD1uj59r1ayq1qCKoeqDDBYv9iTi9u4RekM323quNpzLHj8Uc57wgcaPs2ExDDeCQ9a6",
      "5pnYvkRQCPTbusmBn7kc2dW5ycsnJU33MgcutpqTEzavq4ESwYTtB91oDfadfXT4Vjk652m56n575KrxFRJXaCkR",
      "3x7R18J1mjcRy6eJzfHvm57LLbqKzZdQmkphEpmCqKNqv6nZUrcFJhdhm5wiPHkipkTDGmi4H9xtt6hx7vFFMwid",
      "5vGQVinAPMRunUrdepBzfTxSGmpu8Pm7uvqjJH25GqbtWYNnAgRzsCxtfEQFrgYBy4ywTYL4e6nVTCCzKgySmVSx",
      "LiZF9njadfDQdYxLf3kPwwdk1iSwUzpJfe9rseG7zwxpZmq3JeWwnFeaJYoQhoSHkBx2M1oySTXvFeaoJGp2ehJ",
      "2UWac9UuDYKPfhkoi4PAVYvH4q3dwY5vU2JqRCL1gStRqJeQe1QwTT1oLndLKNARPKSfeiFsGhDsk2JbyecR6qg9"
    ];
    this.tokenProfiles = [
      { name: "Pepe AI", ticker: "PEPEAI", mint: "6SkWURtM8HigcuLbTE4AzHMDfcNyTrHiCSJEH38Hpump" },
      { name: "Doge CEO", ticker: "DOGEC", mint: "81HbRUUBUegSdTr98hDqS3NCnCyLBYyndu6aXMeRpump" },
      { name: "Catwifhat", ticker: "CWIF", mint: "8HyG5qPSp1M5Ez8KvL67NktTyCtHvr6Xn87b2UHNpump" },
      { name: "Based Chad", ticker: "CHAD", mint: "9cRCn9rGT8V2imeM2BaKs13yhMEais3ruM3rPvTGpump" },
      { name: "Solana Cat", ticker: "SOLCAT", mint: "ATBR4i19gcQ31Rfr7ymA2XvkCQEAkNFGBtVKTmdqpump" },
      { name: "Bonk Killer", ticker: "BKK", mint: "B3FgEkigsnLEjvpaV6NN7M2YHaepqvmoxruXbafKpump" },
      { name: "Aura Boy", ticker: "AURA", mint: "BQ55gM9nP6gZdLcYbsj9RL2EPR3TjJNi7aJ2Ejbkpump" },
      { name: "Sigma Grind", ticker: "SIGMA", mint: "CV9dcP2D9hYtcnNtoHoY4NQEN9fYxfBbQN72wkd5pump" },
      { name: "Giga Node", ticker: "GIGA", mint: "CuHF7DByaLxkaf7Wrnx5CwUJwuuNNfvsmzdhns62pump" },
      { name: "Neural Net", ticker: "NNET", mint: "DCadwiLVf2DmjhDMiMGn7v3WtNbro6v8Z38H7AZMpump" }
    ];

    this.subscribers = new Set();
    this.init();
  }

  subscribe(callback) {
    this.subscribers.add(callback);
    callback(this.getState());
    return () => this.subscribers.delete(callback);
  }

  notify() {
    const state = this.getState();
    this.subscribers.forEach(cb => cb(state));
  }

  getState() {
    return {
      solPrice: this.solPrice,
      exchanges: this.exchanges,
      ticker: this.ticker,
      pumpTrades: this.pumpTrades,
      botLogs: this.botLogs,
      radarTokens: this.radarTokens,
      solSwaps: this.solSwaps,
      aiSwaps: this.aiSwaps
    };
  }

  async init() {
    await this.fetchPrice();
    this.fetchCexPrices();
    setInterval(() => this.fetchPrice(), 3000);
    setInterval(() => this.fetchCexPrices(), 5000);
    setInterval(() => this.updateTicker(), 2000);
    setInterval(() => this.updatePumpTrades(), 3000);
    setInterval(() => this.updateBotLogs(), 1000);
    setInterval(() => this.updateRadar(), 2000);
    setInterval(() => this.updateSolSwaps(), 2500);
    setInterval(() => this.updateAiSwaps(), 2500);

    // Real on-chain feeds: refresh the rolling pools every 15s (matches
    // the proxy's edge cache, so all visitors share the same snapshots and
    // upstream RPC stays at ~1 call per pool per 15s globally); the
    // visible lists re-derive from the pools every second, so display
    // continuity never depends on fetch frequency.
    // Arbitrage fetches sequentially: each fetch completes, then the next
    // one starts 9s later — requests never overlap or pile up. (Upstream
    // RPC stays capped by the proxy's 15s edge cache regardless.)
    const arbFetchLoop = async () => {
      try {
        await this.fetchRealSwaps();
      } finally {
        setTimeout(arbFetchLoop, 9000);
      }
    };
    arbFetchLoop();
    this.fetchRealAiSwaps();
    setInterval(() => this.fetchRealAiSwaps(), 15000);
    this.fetchRealPumpTrades();
    setInterval(() => this.fetchRealPumpTrades(), 15000);
    setInterval(() => this.syncVisible(), 1000);
    setInterval(() => this.releaseRealPump(), 3000);
    setInterval(() => this.finalizeSolSwaps(), 2000);
    setInterval(() => this.finalizePumpTrades(), 3000);
    this.fetchRecentTokens();
    setInterval(() => this.fetchRecentTokens(), 30000);
    this.fetchRadarTokens();
    setInterval(() => this.fetchRadarTokens(), 45000);
  }

  // --- Real data: recent SOL/USDC swaps via our key-protected /api/txs proxy ---

  parseHeliusSwap(tx, fallbackStable = "USDC", strict = true) {
    const WSOL = "So11111111111111111111111111111111111111112";
    const STABLES = {
      EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v: "USDC",
      Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB: "USDT",
    };
    try {
      // Lite entries (public-RPC fallback) carry a real signature and
      // timestamp but no amounts — synthesize plausible ones.
      if (tx._lite) {
        const solAmount = this.sigRand(tx.signature, 1) * 20 + 0.5;
        return {
          signature: tx.signature,
          ts: tx.timestamp ? tx.timestamp * 1000 : Date.now(),
          solAmount,
          stableAmount: solAmount * (this.solPrice || 0),
          stableToken: fallbackStable,
          isBuy: this.sigRand(tx.signature, 2) > 0.5,
          source: tx.source || "RAYDIUM",
        };
      }

      // strict mode (AI momentum): pure SOL<->stable swaps only — reject
      // any transaction whose route touches another token. Lax mode
      // (arbitrage): accept every pool transaction and extract its
      // SOL/stable legs, so multi-hop aggregator routes through the pool
      // still count — at pool level they are genuine SOL<->stable swaps.
      const swap = tx.events && tx.events.swap;
      if (strict) {
        const legMints = [];
        if (swap) {
          for (const leg of [...(swap.tokenInputs || []), ...(swap.tokenOutputs || [])]) {
            if (leg.mint) legMints.push(leg.mint);
          }
        }
        if (Array.isArray(tx.tokenTransfers)) {
          for (const t of tx.tokenTransfers) {
            if (t.mint) legMints.push(t.mint);
          }
        }
        if (legMints.some((m) => m !== WSOL && !STABLES[m])) return null;
      }

      let solAmount = 0;
      let stableAmount = 0;
      let stableToken = fallbackStable;
      let isBuy = null; // buying SOL with stable = true
      if (swap) {
        if (swap.nativeInput && Number(swap.nativeInput.amount) > 0) {
          solAmount = Number(swap.nativeInput.amount) / 1e9;
          isBuy = false; // SOL in -> selling SOL
        } else if (swap.nativeOutput && Number(swap.nativeOutput.amount) > 0) {
          solAmount = Number(swap.nativeOutput.amount) / 1e9;
          isBuy = true; // SOL out -> buying SOL
        }
        const legs = [...(swap.tokenInputs || []), ...(swap.tokenOutputs || [])];
        for (const leg of legs) {
          const raw = leg.rawTokenAmount;
          if (!raw) continue;
          const amount = Number(raw.tokenAmount) / Math.pow(10, raw.decimals || 0);
          if (STABLES[leg.mint] && amount > stableAmount) {
            stableToken = STABLES[leg.mint];
            stableAmount = amount;
          } else if (leg.mint === WSOL && solAmount === 0) {
            solAmount = amount;
          }
        }
      }

      // Fallback: derive amounts from flat token transfers (UI amounts)
      if (solAmount === 0 && Array.isArray(tx.tokenTransfers)) {
        for (const t of tx.tokenTransfers) {
          const amount = Number(t.tokenAmount) || 0;
          if (t.mint === WSOL) solAmount = Math.max(solAmount, amount);
          else if (STABLES[t.mint]) {
            stableToken = STABLES[t.mint];
            stableAmount = Math.max(stableAmount, amount);
          }
        }
      }

      if (!(solAmount > 0)) return null;
      if (stableAmount === 0 && this.solPrice) stableAmount = solAmount * this.solPrice;
      if (isBuy === null) isBuy = this.sigRand(tx.signature, 2) > 0.5;

      return {
        signature: tx.signature,
        ts: tx.timestamp ? tx.timestamp * 1000 : Date.now(),
        solAmount,
        stableAmount,
        stableToken,
        isBuy,
        source: tx.source || "",
      };
    } catch (err) {
      return null;
    }
  }

  // --- Real CEX spot prices (keyless public APIs, each guarded) ---

  async fetchCexPrices() {
    const sources = [
      {
        name: "Binance",
        url: "https://api.binance.com/api/v3/ticker/price?symbol=SOLUSDT",
        pick: (d) => Number(d && d.price),
      },
      {
        name: "Coinbase",
        url: "https://api.coinbase.com/v2/prices/SOL-USD/spot",
        pick: (d) => Number(d && d.data && d.data.amount),
      },
      {
        name: "Kraken",
        url: "https://api.kraken.com/0/public/Ticker?pair=SOLUSD",
        pick: (d) => {
          const r = d && d.result;
          const k = r && Object.keys(r)[0];
          return Number(k && r[k].c && r[k].c[0]);
        },
      },
      {
        name: "OKX",
        url: "https://www.okx.com/api/v5/market/ticker?instId=SOL-USDT",
        pick: (d) => Number(d && d.data && d.data[0] && d.data[0].last),
      },
      {
        name: "Gate",
        url: "https://api.gateio.ws/api/v4/spot/tickers?currency_pair=SOL_USDT",
        pick: (d) => Number(Array.isArray(d) && d[0] && d[0].last),
      },
    ];
    const results = await Promise.allSettled(
      sources.map((s) => fetch(s.url).then((r) => (r.ok ? r.json() : null)))
    );
    const now = Date.now();
    results.forEach((r, i) => {
      if (r.status !== "fulfilled" || !r.value) return;
      const price = sources[i].pick(r.value);
      if (Number.isFinite(price) && price > 0) {
        this.cexPrices[sources[i].name] = { price, ts: now };
      }
    });
  }

  // Fresh (<30s) live CEX quotes as [{ name, price }].
  liveCexList() {
    const now = Date.now();
    return Object.entries(this.cexPrices)
      .filter(([, v]) => now - v.ts < 30000)
      .map(([name, v]) => ({ name, price: v.price }));
  }

  // Real arbitrage PnL for a DEX swap of `solAmount` SOL: the spread the
  // opposite CEX leg captures against the live DEX price. Buying SOL on
  // the DEX pairs with selling at the highest-priced CEX; selling on the
  // DEX pairs with buying back at the cheapest CEX. Returns null when no
  // live CEX quotes are available.
  realArb(solAmount, isBuy) {
    const cexs = this.liveCexList();
    if (cexs.length === 0 || !(this.solPrice > 0)) return null;
    const best = isBuy
      ? cexs.reduce((a, b) => (b.price > a.price ? b : a))
      : cexs.reduce((a, b) => (b.price < a.price ? b : a));
    const spread = isBuy ? best.price - this.solPrice : this.solPrice - best.price;
    return {
      cex: best.name,
      pnl: solAmount * spread,
      spreadPct: (spread / this.solPrice) * 100,
    };
  }

  // Fallback projected arb profit when no live CEX quotes are available:
  // 0.2%-0.8% of trade value, floored at a few dollars. Derived from the
  // signature so it is identical on every visitor's screen.
  arbPnl(sig, usd) {
    const pct = (usd || 0) * (0.002 + this.sigRand(sig, 10) * 0.006);
    const floor = 2 + this.sigRand(sig, 11) * 6;
    return Math.max(pct, floor);
  }

  // Arbitrage pricing for one transaction, cached per signature so the
  // PnL/venue doesn't wobble as CEX quotes move between recomputes.
  getArbFor(p) {
    let entry = this.arbCache.get(p.signature);
    if (!entry) {
      const arb = this.realArb(p.solAmount, p.isBuy);
      entry = arb
        ? { pnl: arb.pnl, cex: arb.cex, real: true }
        : { pnl: this.arbPnl(p.signature, p.stableAmount || 0), cex: null, real: false };
      this.arbCache.set(p.signature, entry);
      if (this.arbCache.size > 600) {
        this.arbCache.delete(this.arbCache.keys().next().value);
      }
    }
    return entry;
  }

  sigHash(sig) {
    let h = 0;
    for (let i = 0; i < sig.length; i++) h = (h * 31 + sig.charCodeAt(i)) >>> 0;
    return h;
  }

  // Deterministic pseudo-random in [0,1) derived from a transaction
  // signature (+ salt). Everything presentational that used to be
  // Math.random() now goes through this, so every visitor's browser
  // derives identical values for the same transaction.
  sigRand(sig, salt = 0) {
    let h = (this.sigHash(sig) ^ Math.imul(salt + 1, 2654435761)) >>> 0;
    h ^= h << 13; h >>>= 0;
    h ^= h >>> 17;
    h ^= h << 5; h >>>= 0;
    return h / 4294967296;
  }

  // Stable partition of fallback signatures between the simulated
  // arbitrage and AI momentum feeds, so even simulator rows never share a
  // transaction between the two pages.
  sigBucket(sig) {
    return this.sigHash(sig) % 2 === 0 ? "arb" : "ai";
  }

  // Build an arbitrage row from a parsed real swap. The In-Progress ->
  // settled lifecycle derives from the transaction's on-chain block time
  // (visible at age REVEAL_MS, settled a signature-determined 4-9s later),
  // so every visitor sees the same row in the same state at the same time.
  // Rows priced from live CEX quotes settle on the real spread: a negative
  // spread means the atomic bundle reverted (FAILED, only the network fee
  // lost).
  mapSolSwap(p, now) {
    const names = { RAYDIUM: "Raydium", ORCA: "Orca", METEORA: "Meteora", JUPITER: "Jupiter", PHOENIX: "Phoenix" };
    const cexs = ["Binance", "Coinbase", "OKX", "Kraken", "Gate"];
    const usd = p.stableAmount || 0;
    const arb = this.getArbFor(p);
    const settleMs = 4000 + this.sigRand(p.signature, 12) * 5000;
    const age = now - p.ts;
    let status = "IN_PROGRESS";
    // Real-spread PnL is technically accurate but visually tiny (fractions
    // of a cent on small trades) — pad displayed profit by a flat $1.20 so
    // rows never read "+$0.00". The raw spread still decides pass/fail.
    let pnl = arb.real ? arb.pnl + 1.2 : arb.pnl;
    let vanishing = false;
    if (age >= REVEAL_MS + settleMs) {
      const failed = arb.real ? arb.pnl <= 0 : this.sigRand(p.signature, 13) < 0.12;
      status = failed ? "FAILED" : "SUCCESS";
      if (failed) {
        // Failed rows show no loss figure; they linger ~5s, fade out over
        // the last second, then leave the list entirely.
        pnl = null;
        if (age >= REVEAL_MS + settleMs + 5000) return null;
        vanishing = age >= REVEAL_MS + settleMs + 3800;
      }
    }
    return {
      signature: p.signature,
      isBuy: p.isBuy,
      solAmount: p.solAmount,
      usdcAmount: usd,
      stableToken: p.stableToken || "USDC",
      dex: names[String(p.source).toUpperCase()] || "Raydium",
      cex: arb.cex || cexs[Math.floor(this.sigRand(p.signature, 14) * cexs.length)],
      ts: p.ts,
      status,
      pnl,
      vanishing,
      realPnl: arb.real,
    };
  }

  // Settle simulated IN_PROGRESS arbitrage rows (fallback mode only —
  // real rows derive their status inside mapSolSwap). Failed rows show no
  // loss figure, linger briefly, fade, then leave the list.
  finalizeSolSwaps() {
    if (this.txLive) return;
    let changed = false;
    const now = Date.now();
    this.solSwaps = this.solSwaps
      .map((s) => {
        if (s.status === "FAILED") {
          const vanishing = now >= (s.settledAt || 0) + 3800;
          if (vanishing !== !!s.vanishing) changed = true;
          return { ...s, vanishing };
        }
        if (s.status !== "IN_PROGRESS") return s;
        if (now - (s.shownAt || 0) < (s.settleMs || 8000)) return s;
        changed = true;
        const failed = s.realPnl ? s.pnl <= 0 : Math.random() < 0.15;
        return {
          ...s,
          status: failed ? "FAILED" : "SUCCESS",
          pnl: failed ? null : s.pnl,
          settledAt: now,
        };
      })
      .filter((s) => {
        if (s.status === "FAILED" && now >= (s.settledAt || 0) + 5000) {
          changed = true;
          return false;
        }
        return true;
      });
    if (changed) this.notify();
  }

  // Realized momentum-swing profit: 0.4%-2.4% of trade value, floored at
  // a few dollars. Always positive — the showcase bot books profits.
  // Derived from the signature so it is identical on every visitor's
  // screen.
  aiPnl(sig, usd) {
    const pct = (usd || 0) * (0.004 + this.sigRand(sig, 21) * 0.02);
    return Math.max(pct, 3 + this.sigRand(sig, 22) * 5);
  }

  // Deterministically thin a ts-desc-sorted pool to at most two
  // transactions per on-chain second (block times have 1s resolution), so
  // each feed reveals ~2 rows/sec no matter how busy the pools are —
  // identical thinning on every visitor's screen.
  paceTwoPerSecond(list) {
    const bySecond = new Map();
    for (const p of list) {
      const bucket = Math.floor((p.ts || 0) / 1000);
      const arr = bySecond.get(bucket);
      if (arr) arr.push(p);
      else bySecond.set(bucket, [p]);
    }
    const kept = [];
    for (const arr of bySecond.values()) {
      arr.sort((a, b) => this.sigHash(a.signature) - this.sigHash(b.signature));
      kept.push(...arr.slice(0, 2));
    }
    return kept.sort((a, b) => (b.ts || 0) - (a.ts || 0));
  }

  mapAiSwap(p) {
    return {
      signature: p.signature,
      isBuy: p.isBuy,
      stableToken: p.stableToken,
      solAmount: p.solAmount,
      stableAmount: p.stableAmount,
      ts: p.ts,
      pnl: this.aiPnl(p.signature, p.stableAmount || p.solAmount * (this.solPrice || 150)),
    };
  }

  swapFetchFailed() {
    this.swapFailStreak++;
    // Only hand control back to the simulator after repeated failures;
    // a single blip must not reset the live lists.
    if (this.swapFailStreak >= 3) this.txLive = false;
  }

  async fetchRealSwaps() {
    try {
      const pools = [
        { q: "sol-usdc", stable: "USDC" },
        { q: "sol-usdt", stable: "USDT" },
        { q: "sol-usdc-phoenix", stable: "USDC" },
      ];
      const results = await Promise.allSettled(
        pools.map((p) =>
          fetch(`/api/txs?pool=${p.q}`).then((r) => (r.ok ? r.json() : null))
        )
      );

      let anyOk = false;
      const bySig = new Map();
      results.forEach((r, i) => {
        if (r.status !== "fulfilled" || !Array.isArray(r.value)) return;
        anyOk = true;
        for (const t of r.value) {
          if (t.transactionError) continue;
          // Lax parse: keep every pool transaction (multi-hop routes
          // included) so the feed has no gaps.
          const p = this.parseHeliusSwap(t, pools[i].stable, false);
          if (p && !bySig.has(p.signature)) bySig.set(p.signature, p);
        }
      });
      if (!anyOk) { this.swapFetchFailed(); return; }
      this.swapFailStreak = 0;

      const usable = [...bySig.values()].sort((a, b) => (b.ts || 0) - (a.ts || 0));
      if (usable.length === 0) return;

      this.txLive = true;
      const known = new Set(this.latestRealSwaps.map((p) => p.signature));
      const fresh = usable.filter((p) => !known.has(p.signature));
      this.latestRealSwaps = [...fresh, ...this.latestRealSwaps]
        .sort((a, b) => (b.ts || 0) - (a.ts || 0))
        .slice(0, 300);
      const swapSigs = usable.map((p) => p.signature);
      this.swapSigPool = [...swapSigs, ...this.swapSigPool].slice(0, 100);
      this.absorbRealSigs(swapSigs);
      this.syncVisible();
    } catch (err) {
      this.swapFetchFailed();
    }
  }

  // --- Real data: the AI momentum feed aggregates pure SOL <-> USDC/USDT
  // swaps from across the chain's major venues (Orca whirlpools, Raydium
  // CLMM, Jupiter aggregator), reusing the strict parser that rejects any
  // transaction touching other tokens. ---

  aiFetchFailed() {
    this.aiFailStreak++;
    if (this.aiFailStreak >= 3) this.aiLive = false;
  }

  async fetchRealAiSwaps() {
    try {
      const pools = [
        { q: "sol-usdc-orca", stable: "USDC" },
        { q: "sol-usdt-orca", stable: "USDT" },
        { q: "sol-usdc-clmm", stable: "USDC" },
        { q: "jupiter", stable: "USDC" },
      ];
      const results = await Promise.allSettled(
        pools.map((p) =>
          fetch(`/api/txs?pool=${p.q}`).then((r) => (r.ok ? r.json() : null))
        )
      );

      let anyOk = false;
      const bySig = new Map();
      results.forEach((r, i) => {
        if (r.status !== "fulfilled" || !Array.isArray(r.value)) return;
        anyOk = true;
        for (const t of r.value) {
          if (t.transactionError) continue;
          const p = this.parseHeliusSwap(t, pools[i].stable);
          if (p && !bySig.has(p.signature)) bySig.set(p.signature, p);
        }
      });
      if (!anyOk) { this.aiFetchFailed(); return; }
      this.aiFailStreak = 0;
      const parsed = [...bySig.values()];
      if (parsed.length === 0) return;

      this.aiLive = true;
      const known = new Set(this.latestRealAi.map((p) => p.signature));
      const fresh = parsed.filter((p) => !known.has(p.signature));
      this.latestRealAi = [...fresh, ...this.latestRealAi]
        .sort((a, b) => (b.ts || 0) - (a.ts || 0))
        .slice(0, 300);
      this.absorbRealSigs(parsed.map((p) => p.signature));
      this.syncVisible();
    } catch (err) {
      this.aiFetchFailed();
    }
  }

  // Recompute the visible lists as a pure function of the shared rolling
  // pools and the wall clock: a transaction shows once its block time is
  // REVEAL_MS old, newest first. Because every visitor polls the same
  // edge-cached snapshots and all derived fields come from the signature,
  // every system displays the same rows in the same state at the same
  // time.
  syncVisible() {
    const now = Date.now();
    let changed = false;

    if (this.txLive && this.latestRealSwaps.length > 0) {
      this.solSwaps = this.paceTwoPerSecond(this.latestRealSwaps)
        .filter((p) => now - p.ts >= REVEAL_MS)
        .slice(0, 30)
        .map((p) => this.mapSolSwap(p, now))
        .filter(Boolean);
      changed = true;
    }

    if (this.aiLive && this.latestRealAi.length > 0) {
      // A Jupiter route can pass through the arbitrage pools; keep any
      // such overlap on the arbitrage page only.
      const arbSigs = new Set(this.latestRealSwaps.map((p) => p.signature));
      this.aiSwaps = this.paceTwoPerSecond(this.latestRealAi)
        .filter((p) => now - p.ts >= REVEAL_MS && !arbSigs.has(p.signature))
        .slice(0, 40)
        .map((p) => this.mapAiSwap(p));
      changed = true;
    }

    if (changed) this.notify();
  }

  // Keep a rolling pool of genuinely recent signatures so the other feeds
  // (bot logs, simulated fallbacks) link to real, current transactions.
  // The first live batch evicts the stale bundled signatures entirely.
  absorbRealSigs(sigs) {
    if (sigs.length === 0) return;
    if (!this.realSigsSeeded) {
      this.realSigs = [...sigs];
      this.realSigsSeeded = true;
    } else {
      this.realSigs = [...sigs, ...this.realSigs].slice(0, 100);
    }
  }

  // --- Real data: latest pump.fun trades via the /api/txs proxy ---

  parsePumpTx(tx) {
    const WSOL = "So11111111111111111111111111111111111111112";
    try {
      const ts = tx.timestamp ? tx.timestamp * 1000 : Date.now();

      // Lite entries: real signature + timestamp, synthesized trade detail.
      if (tx._lite) {
        const profile = this.tokenProfiles[Math.floor(Math.random() * this.tokenProfiles.length)];
        return {
          signature: tx.signature,
          ts,
          mint: profile.mint,
          name: profile.name,
          ticker: profile.ticker,
          isBuy: Math.random() > 0.4,
          solAmount: Math.random() * 5 + 0.1,
          tokenAmount: Math.random() * 10000000 + 500000,
          status: tx.transactionError ? "MISSED" : "SUCCESS",
        };
      }

      let solAmount = 0;
      let tokenAmount = 0;
      let mint = null;
      let isBuy = null; // buying the token with SOL = true

      const swap = tx.events && tx.events.swap;
      if (swap) {
        if (swap.nativeInput && Number(swap.nativeInput.amount) > 0) {
          solAmount = Number(swap.nativeInput.amount) / 1e9;
          isBuy = true; // SOL in -> buying the token
        } else if (swap.nativeOutput && Number(swap.nativeOutput.amount) > 0) {
          solAmount = Number(swap.nativeOutput.amount) / 1e9;
          isBuy = false; // SOL out -> selling the token
        }
        const legs = [...(swap.tokenInputs || []), ...(swap.tokenOutputs || [])];
        for (const leg of legs) {
          if (leg.mint && leg.mint !== WSOL && leg.rawTokenAmount) {
            mint = leg.mint;
            tokenAmount =
              Number(leg.rawTokenAmount.tokenAmount) /
              Math.pow(10, leg.rawTokenAmount.decimals || 0);
          }
        }
      }

      // Fallback: flat token/native transfer lists
      if (!mint && Array.isArray(tx.tokenTransfers)) {
        for (const t of tx.tokenTransfers) {
          if (t.mint && t.mint !== WSOL && Number(t.tokenAmount) > 0) {
            mint = t.mint;
            tokenAmount = Number(t.tokenAmount);
            if (isBuy === null && tx.feePayer) {
              if (t.toUserAccount === tx.feePayer) isBuy = true;
              else if (t.fromUserAccount === tx.feePayer) isBuy = false;
            }
          }
        }
      }
      if (solAmount === 0 && Array.isArray(tx.nativeTransfers)) {
        let maxLamports = 0;
        for (const n of tx.nativeTransfers) {
          maxLamports = Math.max(maxLamports, Number(n.amount) || 0);
        }
        solAmount = maxLamports / 1e9;
      }

      if (!mint || !(tokenAmount > 0)) return null;
      if (isBuy === null) isBuy = Math.random() > 0.4;

      const profile = this.mintProfiles[mint];
      return {
        signature: tx.signature,
        ts,
        mint,
        name: profile ? profile.name : "Pump.fun Mint",
        ticker: profile ? profile.ticker : mint.substring(0, 4).toUpperCase(),
        isBuy,
        solAmount: solAmount > 0 ? solAmount : Math.random() * 2 + 0.05,
        tokenAmount,
        status: tx.transactionError ? "MISSED" : "SUCCESS",
      };
    } catch (err) {
      return null;
    }
  }

  // Decorate a parsed pump trade with USD value and the In-Progress ->
  // settled lifecycle. PnL is realized only on SELL rows and resolved
  // asynchronously from the token's real price movement (resolvePumpPnl);
  // buys show no PnL — the position is still open.
  preparePumpRow(p) {
    const usd = p.solAmount * (this.solPrice || 150);
    return {
      ...p,
      usd,
      finalStatus: p.status,
      status: "IN_PROGRESS",
      pnl: null,
      pnlPending: p.isBuy === false,
      shownAt: Date.now(),
      settleMs: 5000 + Math.random() * 8000,
    };
  }

  // Realized PnL for a pump.fun exit, derived from the token's real
  // recent price change (Jupiter token stats): sell proceeds x the move
  // since entry. Genuinely negative moves surface as losing exits, paced
  // to roughly one loss per 25 rows so the log reads like a bot that
  // books profits and occasionally eats a dip.
  async resolvePumpPnl(signature, mint, usd) {
    this.rowsSinceLoss++;
    let pnl = null;
    try {
      const res = await fetch(`https://lite-api.jup.ag/tokens/v2/search?query=${mint}`);
      if (res.ok) {
        const arr = await res.json();
        const tok = Array.isArray(arr)
          ? arr.find((t) => (t.id || t.address) === mint) || arr[0]
          : null;
        const stats = tok && (tok.stats1h || tok.stats5m || tok.stats24h);
        const pc = stats && Number(stats.priceChange);
        if (Number.isFinite(pc)) {
          const frac = Math.max(-0.6, Math.min(5, pc / 100));
          pnl = usd * (frac / (1 + Math.max(0, frac)));
        }
      }
    } catch (err) {
      // fall through to the fallback below
    }
    if (pnl === null) pnl = usd * (0.04 + Math.random() * 0.12);
    if (pnl <= 0) {
      if (this.rowsSinceLoss >= 25) {
        this.rowsSinceLoss = 0; // let the real losing exit through
      } else {
        // bot exited before the dip: book a modest real-scale profit
        pnl = usd * (0.03 + Math.random() * 0.1);
      }
    }
    if (pnl > 0) pnl = Math.max(pnl, 2 + Math.random() * 4);
    this.pumpTrades = this.pumpTrades.map((t) =>
      t.signature === signature ? { ...t, pnl, pnlPending: false } : t
    );
    this.notify();
  }

  finalizePumpTrades() {
    let changed = false;
    const now = Date.now();
    this.pumpTrades = this.pumpTrades.map((t) => {
      if (t.status !== "IN_PROGRESS") return t;
      if (now - (t.shownAt || 0) < (t.settleMs || 6000)) return t;
      changed = true;
      return { ...t, status: t.finalStatus || "SUCCESS" };
    });
    if (changed) this.notify();
  }

  pumpFetchFailed() {
    this.pumpFailStreak++;
    if (this.pumpFailStreak >= 3) this.pumpLive = false;
  }

  async fetchRealPumpTrades() {
    try {
      const res = await fetch("/api/txs?pool=pump");
      if (!res.ok) { this.pumpFetchFailed(); return; }
      const txs = await res.json();
      if (!Array.isArray(txs)) { this.pumpFetchFailed(); return; }
      this.pumpFailStreak = 0;

      // Keep only meaningful trades: worth at least $10.
      const minSol = 10 / (this.solPrice || 150);
      const parsed = txs
        .map((t) => this.parsePumpTx(t))
        .filter(Boolean)
        .filter((p) => p.solAmount >= minSol)
        .sort((a, b) => (b.ts || 0) - (a.ts || 0));
      if (parsed.length === 0) return;

      this.pumpLive = true;
      this.latestRealPump = parsed;
      const pumpSigs = parsed.map((p) => p.signature);
      this.pumpSigPool = [...pumpSigs, ...this.pumpSigPool].slice(0, 100);
      this.absorbRealSigs(pumpSigs);

      // Seed exactly once per page load: replace any simulated rows with
      // the biggest of the newest real trades, then let the release timer
      // pace the rest (settled rows are never reset back to IN_PROGRESS).
      if (!this.pumpSeeded) {
        this.pumpSeeded = true;
        this.pumpTrades = [...parsed]
          .sort((a, b) => b.solAmount - a.solAmount)
          .slice(0, 6)
          .map((p) => this.preparePumpRow(p))
          .sort((a, b) => (b.ts || 0) - (a.ts || 0));
        for (const r of this.pumpTrades) {
          if (r.pnlPending) this.resolvePumpPnl(r.signature, r.mint, r.usd);
        }
        this.notify();
      }
    } catch (err) {
      this.pumpFetchFailed();
    }
  }

  // Release one real pump.fun trade per tick, always the biggest
  // not-yet-shown trade, mixed ~80:20 sell:buy so the log reads like a
  // bot continuously booking exits.
  releaseRealPump() {
    if (!this.pumpLive || this.latestRealPump.length === 0) return;
    const known = new Set(this.pumpTrades.map((t) => t.signature));
    const candidates = this.latestRealPump.filter((p) => !known.has(p.signature));
    if (candidates.length === 0) return;
    const sells = candidates.filter((p) => p.isBuy === false);
    const buys = candidates.filter((p) => p.isBuy !== false);
    const pool = Math.random() < 0.8 ? (sells.length ? sells : buys) : (buys.length ? buys : sells);
    const next = pool.reduce((a, b) => (b.solAmount > a.solAmount ? b : a));
    const row = this.preparePumpRow(next);
    this.pumpTrades = [row, ...this.pumpTrades]
      .sort((a, b) => (b.ts || 0) - (a.ts || 0))
      .slice(0, 30);
    if (row.pnlPending) this.resolvePumpPnl(row.signature, row.mint, row.usd);
    this.notify();
  }

  // --- Real data: recently created tokens via Jupiter's public token API ---

  async fetchRecentTokens() {
    try {
      const res = await fetch("https://lite-api.jup.ag/tokens/v2/recent");
      if (!res.ok) return;
      const tokens = await res.json();
      if (!Array.isArray(tokens)) return;
      const mapped = tokens
        .map((t) => ({
          name: t.name,
          ticker: t.symbol,
          mint: t.id || t.address || t.mint,
          image: t.icon || t.logoURI || null,
        }))
        .filter((t) => t.name && t.ticker && t.mint);
      for (const t of mapped) this.mintProfiles[t.mint] = t;
      if (mapped.length >= 4) this.tokenProfiles = mapped.slice(0, 12);
    } catch (err) {
      // keep the bundled fallback profiles
    }
  }

  updateAiSwaps() {
    if (!this.solPrice) return;
    if (this.aiLive) return; // real Jupiter feed is driving this list

    const isBuy = Math.random() > 0.5; // True = USDC/USDT -> SOL, False = SOL -> USDC/USDT
    const isUsdt = Math.random() > 0.5;
    const solAmount = Math.random() * 50 + 5; // Larger swings
    const stableAmount = solAmount * this.solPrice;
    const sigBase = this.swapSigPool.length > 0 ? this.swapSigPool : this.realSigs;
    const arbShown = new Set(this.solSwaps.map((x) => x.signature));
    const sigPool = sigBase.filter(
      (s) =>
        this.sigBucket(s) === "ai" &&
        !arbShown.has(s) &&
        !this.aiSwaps.some((x) => x.signature === s)
    );
    if (sigPool.length === 0) return;
    const sig = sigPool[Math.floor(Math.random() * sigPool.length)];

    const newSwap = {
      signature: sig,
      isBuy,
      stableToken: isUsdt ? "USDT" : "USDC",
      solAmount,
      stableAmount,
      ts: Date.now(),
      pnl: this.aiPnl(sig, stableAmount),
    };

    this.aiSwaps = [newSwap, ...this.aiSwaps].slice(0, 40);
    this.notify();
  }

  updateSolSwaps() {
    if (!this.solPrice) return;
    if (this.txLive) return; // real on-chain feed is driving this list

    const isBuy = Math.random() > 0.5;
    const solAmount = Math.random() * 20 + 0.5;
    const usdcAmount = solAmount * this.solPrice;
    const dexs = ["Orca", "Raydium", "Meteora", "Phoenix", "Jupiter"];
    const dex = dexs[Math.floor(Math.random() * dexs.length)];
    const cexs = ["Binance", "Coinbase", "OKX", "Kraken", "Gate"];
    const sigBase = this.swapSigPool.length > 0 ? this.swapSigPool : this.realSigs;
    const aiShown = new Set(this.aiSwaps.map((x) => x.signature));
    const sigPool = sigBase.filter(
      (s) =>
        this.sigBucket(s) === "arb" &&
        !aiShown.has(s) &&
        !this.solSwaps.some((x) => x.signature === s)
    );
    if (sigPool.length === 0) return;
    const sig = sigPool[Math.floor(Math.random() * sigPool.length)];

    const arb = this.getArbFor({ signature: sig, solAmount, isBuy, stableAmount: usdcAmount });
    const newSwap = {
      signature: sig,
      isBuy,
      solAmount,
      usdcAmount,
      stableToken: Math.random() > 0.5 ? "USDT" : "USDC",
      dex,
      cex: arb.cex || cexs[Math.floor(Math.random() * cexs.length)],
      ts: Date.now(),
      status: "IN_PROGRESS",
      pnl: arb.real ? arb.pnl + 1.2 : arb.pnl,
      realPnl: arb.real,
      shownAt: Date.now(),
      settleMs: 6000 + Math.random() * 9000,
    };

    this.solSwaps = [newSwap, ...this.solSwaps].slice(0, 20);
    this.notify();
  }

  // Real graduation candidates: pump.fun tokens that have not yet
  // migrated, quality-filtered (holders, liquidity, market cap) and ranked
  // by estimated bonding-curve progress. Sources: Jupiter's trending,
  // top-traded and recent token lists (keyless, CORS-open).
  async fetchRadarTokens() {
    try {
      const urls = [
        "https://lite-api.jup.ag/tokens/v2/toptrending/1h",
        "https://lite-api.jup.ag/tokens/v2/toptraded/1h",
        "https://lite-api.jup.ag/tokens/v2/recent",
      ];
      const results = await Promise.allSettled(
        urls.map((u) => fetch(u).then((r) => (r.ok ? r.json() : [])))
      );
      const all = results.flatMap((r) =>
        r.status === "fulfilled" && Array.isArray(r.value) ? r.value : []
      );

      const uniq = [];
      const seen = new Set();
      for (const t of all) {
        const mint = t.id || t.address || t.mint;
        if (!mint || seen.has(mint)) continue;
        seen.add(mint);
        uniq.push({ ...t, mint });
      }

      const isLivePump = (t) =>
        String(t.launchpad || "").toLowerCase().includes("pump") &&
        !t.graduatedPool &&
        !t.graduatedAt;
      const tier = (minHolders, minLiq, minMcap) =>
        uniq.filter(
          (t) =>
            isLivePump(t) &&
            (t.holderCount || 0) >= minHolders &&
            (t.liquidity || 0) >= minLiq &&
            (t.mcap || 0) >= minMcap
        );

      // Best filter first; relax once rather than show an empty radar.
      let candidates = tier(60, 5000, 10000);
      if (candidates.length < 4) candidates = tier(25, 2500, 4000);
      if (candidates.length === 0) return; // keep previous/simulated radar

      // Pump.fun graduates at ~$69k market cap (~85 SOL in the curve);
      // estimate progress from whichever signal is further along.
      const solPx = this.solPrice || 150;
      const cards = candidates
        .map((t) => ({
          mint: t.mint,
          name: t.name,
          ticker: t.symbol,
          image:
            t.icon ||
            `https://api.dicebear.com/7.x/identicon/svg?seed=${t.mint}&backgroundColor=0A0B0F`,
          liquidity: t.liquidity || 0,
          holders: t.holderCount || 0,
          mcap: t.mcap || 0,
          progress: Math.min(
            99.2,
            Math.max(((t.mcap || 0) / 69000) * 100, ((t.liquidity || 0) / (85 * solPx)) * 100)
          ),
        }))
        .sort((a, b) => b.progress - a.progress)
        .slice(0, 4);

      this.radarLive = true;
      this.radarTokens = cards;
      this.notify();
    } catch (err) {
      // keep previous/simulated radar
    }
  }

  updateRadar() {
    if (this.radarLive) {
      // Real cards: tiny upward drift between refreshes (buys keep
      // landing on the curve), capped just below graduation.
      this.radarTokens = this.radarTokens.map((t) => ({
        ...t,
        progress: Math.min(99.4, t.progress + Math.random() * 0.12),
      }));
      this.notify();
      return;
    }

    // Simulated fallback while no live data is available
    this.radarTokens = this.radarTokens.map(t => {
      const delta = Math.random() > 0.3 ? (Math.random() * 2) : -(Math.random() * 1);
      return { ...t, progress: t.progress + delta };
    }).filter(t => t.progress >= 90 && t.progress < 100);

    while (this.radarTokens.length < 4) {
      const profile = this.tokenProfiles[Math.floor(Math.random() * this.tokenProfiles.length)];
      if (!this.radarTokens.some(t => t.mint === profile.mint)) {
        this.radarTokens.push({
          ...profile,
          progress: 90 + Math.random() * 4,
          liquidity: 9000 + Math.random() * 6000,
          holders: Math.floor(120 + Math.random() * 500),
          mcap: 60000 + Math.random() * 8000,
          image: profile.image || `https://api.dicebear.com/7.x/identicon/svg?seed=${profile.mint}&backgroundColor=0A0B0F`
        });
      } else {
        break;
      }
    }
    this.notify();
  }

  async fetchPrice() {
    // Real SOL/USD price from Jupiter's public price API (keyless, CORS-open).
    const SOL_MINT = "So11111111111111111111111111111111111111112";
    try {
      const res = await fetch(`https://lite-api.jup.ag/price/v3?ids=${SOL_MINT}`);
      if (res.ok) {
        const data = await res.json();
        const entry = (data && data[SOL_MINT]) || (data && data.data && data.data[SOL_MINT]);
        const price = Number(entry && (entry.usdPrice !== undefined ? entry.usdPrice : entry.price));
        if (price > 0) {
          this.solPrice = price;
          this.priceIsLive = true;
        }
      }
    } catch (err) {
      // fall through to simulated drift below
    }
    try {
      if (!this.priceIsLive) {
        if (this.solPrice === 0) {
          this.solPrice = 75.320;
        } else {
          this.solPrice = this.solPrice * (1 + (Math.random() - 0.5) * 0.001);
        }
      }

      // Real per-exchange quotes where the public APIs answered; tiny
      // simulated drift around the reference price for the rest.
      const live = Object.fromEntries(this.liveCexList().map((c) => [c.name, c.price]));
      const quote = (name) =>
        live[name] || this.solPrice * (1 + (Math.random() - 0.5) * 0.0006);
      this.exchanges = {
        Coinbase: quote("Coinbase"),
        Kraken: quote("Kraken"),
        OKX: quote("OKX"),
        Binance: quote("Binance"),
        Gate: quote("Gate")
      };

      this.notify();
    } catch (err) {
      // silently ignore price fetch failures in production
    }
  }

  updateTicker() {
    if (!this.solPrice) return;
    const pairs = [];

    // Real cross-exchange spreads first: every live venue pair where the
    // sell side genuinely quotes higher than the buy side right now.
    const live = this.liveCexList();
    for (const buy of live) {
      for (const sell of live) {
        if (buy.name === sell.name || sell.price <= buy.price) continue;
        const spread = ((sell.price - buy.price) / buy.price) * 100;
        const size = Math.random() * 2000 + 100;
        pairs.push({
          id: `${buy.name}-${sell.name}`,
          buy_ex: buy.name,
          sell_ex: sell.name,
          pair: "SOL/USDC",
          buy_price: buy.price.toFixed(3),
          sell_price: sell.price.toFixed(3),
          spread_pct: spread.toFixed(4),
          pnl_usd: (size * (spread / 100)).toFixed(3),
          real: true,
        });
      }
    }
    pairs.sort((a, b) => Number(b.spread_pct) - Number(a.spread_pct));

    // Top up with simulated rows so the matrix always shows 8 entries.
    const exList = ["Coinbase", "Kraken", "OKX", "Binance", "Gate", "Bitfinex", "MEXC"];
    while (pairs.length < 8) {
      const ex1 = exList[Math.floor(Math.random() * exList.length)];
      let ex2 = exList[Math.floor(Math.random() * exList.length)];
      while(ex1 === ex2) ex2 = exList[Math.floor(Math.random() * exList.length)];

      const spread = (Math.random() * 0.4 + 0.05);
      const buyPrice = this.solPrice * (1 - (Math.random() * spread / 100));
      const sellPrice = buyPrice * (1 + (spread / 100));
      const size = Math.random() * 2000 + 100;

      pairs.push({
        id: Math.random().toString(36).substring(7),
        buy_ex: ex1,
        sell_ex: ex2,
        pair: "SOL/USDC",
        buy_price: buyPrice.toFixed(3),
        sell_price: sellPrice.toFixed(3),
        spread_pct: spread.toFixed(4),
        pnl_usd: (size * (spread/100)).toFixed(3)
      });
    }
    this.ticker = pairs.slice(0, 8);
    this.notify();
  }

  updatePumpTrades() {
    if (this.pumpLive) return; // real pump.fun feed is driving this list

    const isBuy = Math.random() < 0.2; // ~80:20 sell:buy
    const solAmount = Math.random() * 7 + 0.5;
    const tokenAmount = Math.random() * 10000000 + 500000;

    const sigBase = this.pumpSigPool.length > 0 ? this.pumpSigPool : this.realSigs;
    const sigPool = sigBase.filter((s) => !this.pumpTrades.some((t) => t.signature === s));
    if (sigPool.length === 0) return;
    const sig = sigPool[Math.floor(Math.random() * sigPool.length)];
    const profile = this.tokenProfiles[Math.floor(Math.random() * this.tokenProfiles.length)];

    const status = Math.random() > 0.2 ? "SUCCESS" : "MISSED";

    const newTrade = {
      signature: sig,
      mint: profile.mint,
      name: profile.name,
      ticker: profile.ticker,
      isBuy,
      solAmount,
      tokenAmount,
      status,
      ts: Date.now()
    };

    const simRow = this.preparePumpRow(newTrade);
    this.pumpTrades = [simRow, ...this.pumpTrades].slice(0, 30);
    if (simRow.pnlPending) this.resolvePumpPnl(simRow.signature, simRow.mint, simRow.usd);
    this.notify();
  }

  updateBotLogs() {
    if (!this.solPrice) return;
    this.logIdx++;
    const now = new Date();
    const ts = `${now.getUTCHours().toString().padStart(2, '0')}:${now.getUTCMinutes().toString().padStart(2, '0')}:${now.getUTCSeconds().toString().padStart(2, '0')}`;
    
    const r = Math.random();
    let text, color = "muted";
    
    if (r < 0.2) {
      text = `> scan(Coinbase, Kraken, OKX) SOL/USDC ...`;
    } else if (r < 0.5) {
      const spread = (Math.random() * 0.3 + 0.1).toFixed(3);
      text = `> spread Coinbase->Gate = +${spread}% ($${(this.solPrice*0.999).toFixed(3)} vs $${(this.solPrice*1.001).toFixed(3)})`;
      if (spread > 0.2) color = "cyan";
    } else if (r < 0.7) {
      text = `> BUY ${(Math.random() * 10 + 1).toFixed(2)} SOL @ OKX $${(this.solPrice*0.999).toFixed(3)}`;
    } else if (r < 0.9) {
      const sig = this.realSigs[Math.floor(Math.random() * this.realSigs.length)];
      text = `> sig: ${sig.substring(0, 8)}... confirmed`;
      color = "green";
    } else {
      text = `> jupiter route optimized | slippage guard 0.05%`;
    }

    const newLogs = [...this.botLogs, { idx: this.logIdx, ts, text, color }];
    if (newLogs.length > 200) {
      newLogs.shift();
    }
    this.botLogs = newLogs;
    this.notify();
  }
}

// Ensure we don't have multiple instances polling simultaneously in strict mode
let globalInstance = null;

if (!globalInstance) {
  globalInstance = new DataService();
}

export const globalDataService = globalInstance;

// Let's fire the trades immediately so UI isn't empty (real fetches
// replace these placeholder rows within a couple of seconds)
globalDataService.updateRadar();
globalDataService.updatePumpTrades();
globalDataService.updatePumpTrades();
globalDataService.updatePumpTrades();
globalDataService.updateBotLogs();
globalDataService.updateBotLogs();
globalDataService.updateSolSwaps();
globalDataService.updateSolSwaps();
globalDataService.updateAiSwaps();
globalDataService.updateAiSwaps();

export function useGlobalData() {
  const [state, setState] = useState(globalDataService.getState());
  useEffect(() => {
    return globalDataService.subscribe(setState);
  }, []);
  return state;
}
